import { GestureResponderEvent, LayoutChangeEvent, StyleSheet, View, ViewProps } from 'react-native'
import { Positionable, Position as StringPosition } from 'zens-positionable'
import { ReactElement, Ref, useImperativeHandle, useState } from 'react'

import Decorator from './decorator'

const styles = StyleSheet.create({
  drag: {
    cursor: 'pointer',
  },
  content: {
    position: 'absolute',
  },
  overlay: {
    alignItems: 'center',
    justifyContent: 'center',
  },
})

export interface Position {
  x: number
  y: number
}

export type Placement = Position & {
  rotation: number
}

export interface Props extends ViewProps {
  placement?: Placement
  isSelected?: boolean
  onSelect?: (event: MouseEvent) => void
  onUpdate?: (placement: Placement) => void
  onRemove?: (event: GestureResponderEvent) => void
  scale?: number
  snapTo?: number
  overlay?: ReactElement
  disabled?: boolean
  test_ref?: Ref<any>
}

const PositionableView = (props: Props) => {
  const {
    isSelected,
    onSelect,
    onUpdate,
    onLayout,
    onRemove,
    scale,
    snapTo,
    overlay,
    disabled,
    test_ref,
    style,
    children,
  } = props
  const { y = 0, x = 0, rotation = 0 } = props.placement || {}
  const [layout, setLayout] = useState({
    left: `${x}`,
    top: `${y}`,
    width: `0`,
    height: `0`,
    rotation: `${rotation}deg`,
  })

  useImperativeHandle(test_ref, () => ({
    update: (value: StringPosition) => {
      update(value)
    },
    layout: (event: LayoutChangeEvent) => {
      updateLayout(event)
    },
  }))

  const updateLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout
    setLayout({
      ...layout,
      width: `${width}`,
      height: `${height}`,
    })
    onLayout?.(event)
  }

  const snapRotation = (value: string) => {
    const deg = value.match(/^(.+)\s*deg/)?.[1]
    return `${Math.round(parseInt(deg || '0', 10))}deg`
  }

  const update = (value: StringPosition) => {
    setLayout(value)
    const deg = value.rotation?.match(/^(.+)\s*deg/)?.[1]
    onUpdate?.({
      y: parseInt(value.top, 10),
      x: parseInt(value.left, 10),
      rotation: Math.round(parseInt(deg || '0', 10)),
    })
  }

  function renderContent() {
    return (
      <View style={[styles.content, style]} onLayout={updateLayout} testID="Positionable.Content">
        {children}
      </View>
    )
  }

  return (
    <Positionable
      movable
      rotatable
      position={layout}
      onUpdate={update}
      onClick={onSelect}
      snapTo={snapTo}
      group={isSelected ? 'selected' : undefined}
      disabled={disabled}
      render={({ renderedPosition, refHandlers }) => {
        const isRotatingNow = renderedPosition.rotation !== layout.rotation
        const isDraggingNow = renderedPosition.left !== layout.left || renderedPosition.top !== layout.top
        return (
          <>
            {disabled ? (
              <View
                pointerEvents="none"
                style={{
                  height: parseInt(renderedPosition.height, 10),
                  left: parseInt(renderedPosition.left, 10),
                  position: 'absolute' as const,
                  top: parseInt(renderedPosition.top, 10),
                  width: parseInt(renderedPosition.width, 10),
                  transform: [{ rotate: snapRotation(renderedPosition.rotation) }],
                }}
              >
                {renderContent()}
              </View>
            ) : (
              <Decorator
                refHandlers={refHandlers}
                style={[
                  styles.drag,
                  {
                    height: parseInt(renderedPosition.height, 10),
                    left: parseInt(renderedPosition.left, 10),
                    position: 'absolute' as const,
                    top: parseInt(renderedPosition.top, 10),
                    width: parseInt(renderedPosition.width, 10),
                    transform: [{ rotate: snapRotation(renderedPosition.rotation) }],
                  },
                ]}
                isRotating={isRotatingNow}
                isDragging={isDraggingNow}
                isSelected={isSelected}
                scale={scale}
                testID="Positionable.Decorator"
                onRemove={onRemove}
              >
                {renderContent()}
              </Decorator>
            )}
            {overlay && (
              <View
                style={[
                  styles.overlay,
                  {
                    position: 'absolute' as const,
                    left: parseInt(renderedPosition.left, 10),
                    top: parseInt(renderedPosition.top, 10),
                    width: parseInt(renderedPosition.width, 10),
                    height: parseInt(renderedPosition.height, 10),
                  },
                ]}
                pointerEvents="none"
              >
                {overlay}
              </View>
            )}
          </>
        )
      }}
    />
  )
}

export default PositionableView
